import isEmpty from 'lodash-es/isEmpty'
import { useMemo } from 'react'

import {
  CustomerProfileTextEmbroiderySettings,
  ProductCatalogEmbroideryPlacement,
} from '../../__generated__/graphql/catalog/graphql'
import { useEmbroideryPreferences } from '../../app/_hooks/useEmbroideryPreferences'
import { isDefinedAndNotNull } from '../../app/_utils/isDefinedAndNotNull'
import { EmbroiderableProductEmbroideryConfig } from '../../types/graphql/product'

import { EmbroideryFormSubmissions, EmbroideryFormValues, LogoFile } from './types'
import {
  genericTextEmbroideryFonts,
  isGenericTextEmbroideryFontKey,
} from './utils/embroideryThreadOptions'
import { isEmbroideryFontAllowedForProduct } from './utils/isEmbroideryFontAllowedForProduct'

type SavedEmbroideryPlacement = Pick<ProductCatalogEmbroideryPlacement, 'location' | 'position'>
type SavedTextPreference = Pick<
  CustomerProfileTextEmbroiderySettings,
  'firstLine' | 'secondLine' | 'fontKey'
> & {
  placement: SavedEmbroideryPlacement
}
type EmbroideryDefaultFormValues = {
  [key in keyof EmbroideryFormValues]?: Partial<EmbroideryFormValues[key]>
}
type CompatibleSavedTextPreference = Pick<
  EmbroideryFormValues['TEXT'],
  'firstLine' | 'secondLine' | 'textPlacement' | 'font'
>

/**
 * Prefer the saved placement when it is valid for the current product; otherwise fall back
 * to the first available placement from the product config.
 */
const resolvePreferredPlacementOrDefault = ({
  availablePlacements,
  preferredPlacement,
}: {
  availablePlacements: SavedEmbroideryPlacement[] | null | undefined
  preferredPlacement: SavedEmbroideryPlacement | null | undefined
}) => {
  if (!availablePlacements?.length) return undefined

  const preferredPlacementMatch = availablePlacements.find(placement => {
    return (
      preferredPlacement &&
      placement.location === preferredPlacement.location &&
      placement.position === preferredPlacement.position
    )
  })

  return preferredPlacementMatch ?? availablePlacements[0]
}

const normalizeSavedFontStyleKey = (fontKey: string | null | undefined) => {
  if (!fontKey) return undefined

  // isGenericTextEmbroideryFontKey expects the argument to be capitalized
  const capitalizedKey = `${fontKey.slice(0, 1).toUpperCase()}${fontKey.slice(1).toLowerCase()}`
  return isGenericTextEmbroideryFontKey(capitalizedKey) ? capitalizedKey : undefined
}

const trimSavedTextPreference = (text: string | null | undefined, maxCharacterCount: number) => {
  if (!text) return undefined

  return text.slice(0, maxCharacterCount)
}

/**
 * Builds the subset of saved text embroidery preferences that fit the current product's
 * text constraints, such as allowed placement, character limit, font rules, etc. If
 * the preference doesn't work with the product, use a default option instead of dropping
 * the preference.
 */
const getCompatibleSavedTextPreferenceForProduct = ({
  savedTextPreferences,
  productEmbroideryConfig,
}: {
  savedTextPreferences: SavedTextPreference | null
  productEmbroideryConfig: EmbroiderableProductEmbroideryConfig | null | undefined
}): CompatibleSavedTextPreference | undefined => {
  const textEmbroideryOption = productEmbroideryConfig?.options.text
  if (!textEmbroideryOption || !savedTextPreferences) return undefined

  const preferredTextPlacement = resolvePreferredPlacementOrDefault({
    availablePlacements: textEmbroideryOption.placements,
    preferredPlacement: savedTextPreferences.placement,
  })

  const textCharacterLimit = textEmbroideryOption.characterLimit
  const firstLine =
    trimSavedTextPreference(savedTextPreferences.firstLine, textCharacterLimit) ?? ''
  const secondLine =
    textEmbroideryOption.lineLimit >= 2
      ? trimSavedTextPreference(savedTextPreferences.secondLine, textCharacterLimit) ?? ''
      : ''

  if (isEmpty(firstLine.trim()) && isEmpty(secondLine.trim())) return undefined

  const compatibleSavedTextPreference: Partial<CompatibleSavedTextPreference> = {
    firstLine,
    secondLine,
  }

  if (preferredTextPlacement) {
    compatibleSavedTextPreference.textPlacement = preferredTextPlacement
  }

  const savedTextFontKey = normalizeSavedFontStyleKey(savedTextPreferences.fontKey)
  const isFontAllowedForProduct =
    savedTextFontKey &&
    isEmbroideryFontAllowedForProduct({
      fontKey: savedTextFontKey,
      productEmbroideryConfig,
    })

  if (isFontAllowedForProduct) {
    compatibleSavedTextPreference.font = genericTextEmbroideryFonts[savedTextFontKey]
  }

  return compatibleSavedTextPreference as CompatibleSavedTextPreference
}

type UseProductScopedEmbroideryPreferencesProps = {
  defaultFormValues: EmbroideryDefaultFormValues | undefined
  enableEmbroideryPreferences: boolean
  productDefaultFormValuesByKind: Partial<EmbroideryFormValues>
  productEmbroideryConfig: EmbroiderableProductEmbroideryConfig | null | undefined
  transientLogo: LogoFile | undefined
  userLogos: LogoFile[]
}

export function useProductScopedEmbroideryPreferences({
  defaultFormValues,
  enableEmbroideryPreferences,
  productDefaultFormValuesByKind,
  productEmbroideryConfig,
  transientLogo,
  userLogos,
}: UseProductScopedEmbroideryPreferencesProps) {
  const { data, previousData, loading } = useEmbroideryPreferences({
    skip: !enableEmbroideryPreferences,
  })
  const savedEmbroideryPreferences = data ?? previousData

  const compatibleSavedTextPreference = useMemo(() => {
    if (!enableEmbroideryPreferences) return undefined

    if (!savedEmbroideryPreferences) return undefined

    // Saved preferences are only useful if they still fit the current product's placement,
    // font, character-count, and line-count constraints.
    return getCompatibleSavedTextPreferenceForProduct({
      savedTextPreferences: savedEmbroideryPreferences.text,
      productEmbroideryConfig,
    })
  }, [enableEmbroideryPreferences, productEmbroideryConfig, savedEmbroideryPreferences])

  // checks that the logo exists in user's account, is approved, and has a valid placement for this product.
  const compatibleSavedLogoPreference = useMemo(() => {
    if (!enableEmbroideryPreferences) return undefined

    if (!savedEmbroideryPreferences?.logo?.defaultLogoId || !productEmbroideryConfig?.options.logo)
      return undefined

    const availableLogos = [...userLogos, transientLogo].filter(isDefinedAndNotNull)
    const preferredLogo = availableLogos.find(
      logo => logo.id === savedEmbroideryPreferences.logo!.defaultLogoId
    )

    // Pending-approval logos must be treated as no preference
    if (!preferredLogo || preferredLogo.status !== 'APPROVED') return undefined

    const preferredLogoPlacement = resolvePreferredPlacementOrDefault({
      availablePlacements: productEmbroideryConfig.options.logo.placements,
      preferredPlacement: savedEmbroideryPreferences.logo.placement,
    })

    // A logo with no resolvable placement for this product can't be added to the bag
    // (logoPlacement is required on the submission), so treat it as no preference.
    if (!preferredLogoPlacement) return undefined

    return { preferredLogo, preferredLogoPlacement }
  }, [
    enableEmbroideryPreferences,
    productEmbroideryConfig?.options.logo,
    savedEmbroideryPreferences?.logo,
    transientLogo,
    userLogos,
  ])

  // Form defaults derived from saved account preferences. This is what lets the embroidery forms
  // open prefilled with the user's saved values when those values are valid for the product.
  // This is form-shaped data, not preview/summary data.
  const savedEmbroideryDefaultFormValues = useMemo<EmbroideryDefaultFormValues>(() => {
    if (!enableEmbroideryPreferences) return {}

    if (!savedEmbroideryPreferences || !productEmbroideryConfig) {
      return {}
    }

    const savedPreferenceDefaultsByKind: EmbroideryDefaultFormValues = {}

    if (productEmbroideryConfig.options.text && compatibleSavedTextPreference) {
      savedPreferenceDefaultsByKind.TEXT = compatibleSavedTextPreference
    }

    const { icon: savedIconPreferences } = savedEmbroideryPreferences

    if (productEmbroideryConfig.options.icon && savedIconPreferences?.initials) {
      savedPreferenceDefaultsByKind.ICON = {
        initials: savedIconPreferences.initials,
      }
    }

    if (compatibleSavedLogoPreference) {
      savedPreferenceDefaultsByKind.LOGO_DTC = {
        logoFileId: compatibleSavedLogoPreference.preferredLogo.id,
        logoFile: compatibleSavedLogoPreference.preferredLogo,
        logoPlacement: compatibleSavedLogoPreference.preferredLogoPlacement,
      }
    }

    return savedPreferenceDefaultsByKind
  }, [
    compatibleSavedLogoPreference,
    compatibleSavedTextPreference,
    enableEmbroideryPreferences,
    productEmbroideryConfig,
    savedEmbroideryPreferences,
  ])

  // This is used for rendering the preferences on the PDP and allowing users to select if they
  // want to add their embroidery preferences without going into the embroidery modal.
  const compatibleSavedEmbroideryPreferences = useMemo<Partial<EmbroideryFormSubmissions>>(() => {
    if (!enableEmbroideryPreferences) return {}

    const result: Partial<EmbroideryFormSubmissions> = {}

    if (productEmbroideryConfig?.options.text && compatibleSavedTextPreference) {
      const baseTextFormValues = {
        ...productDefaultFormValuesByKind.TEXT,
        ...(defaultFormValues?.TEXT ?? {}),
      }
      const resolvedTextFont = compatibleSavedTextPreference.font ?? baseTextFormValues.font
      const resolvedTextPlacement =
        compatibleSavedTextPreference.textPlacement ?? baseTextFormValues.textPlacement
      const resolvedTextSecondLine =
        productEmbroideryConfig.options.text.lineLimit >= 2
          ? compatibleSavedTextPreference.secondLine
          : undefined

      result.TEXT = {
        textColor: baseTextFormValues.textColor, // currently no color prefs
        textFirstLine: compatibleSavedTextPreference.firstLine,
        textFont: resolvedTextFont,
        textPlacement: resolvedTextPlacement,
        ...(resolvedTextSecondLine ? { textSecondLine: resolvedTextSecondLine } : {}),
      }
    }

    if (compatibleSavedLogoPreference) {
      const { preferredLogo, preferredLogoPlacement } = compatibleSavedLogoPreference
      result.LOGO_DTC = {
        logoId: preferredLogo.id,
        logoFile: preferredLogo,
        logoPreview: preferredLogo.preview,
        logoColor: preferredLogo.digitizationSetting,
        logoName: preferredLogo.name,
        logoNote: preferredLogo.notes ?? undefined,
        logoPlacement: preferredLogoPlacement,
      }
    }

    return result
  }, [
    compatibleSavedLogoPreference,
    compatibleSavedTextPreference,
    defaultFormValues,
    enableEmbroideryPreferences,
    productEmbroideryConfig,
    productDefaultFormValuesByKind.TEXT,
  ])

  return {
    loading,
    savedEmbroideryDefaultFormValues: !isEmpty(savedEmbroideryDefaultFormValues)
      ? savedEmbroideryDefaultFormValues
      : null,
    savedEmbroideryPreferences: !isEmpty(compatibleSavedEmbroideryPreferences)
      ? compatibleSavedEmbroideryPreferences
      : null,
  }
}
